import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Spinner,
  Alert
} from "reactstrap"
import Chart from "react-apexcharts"
import {
  getRFQFinancialInsights,
  getInvoiceAnalyticsGraphData,
  formatCurrency,
  getChartColors
} from "../../../../helpers/rfq_dashboard_api"
import FinancialDetailModal from "./FinancialDetailModal"
import InfoButton from "./InfoButton"
import "./FinancialInsights.css"

const FinancialInsights = ({ filters, onRefresh }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')
  const [modalTitle, setModalTitle] = useState('')
  const [modalData, setModalData] = useState({})

  // Handle card click to open modal
  const handleCardClick = (type, title, additionalData = {}) => {
    setModalType(type)
    setModalTitle(title)
    setModalData(additionalData)
    setModalOpen(true)
  }

  // Handle chart click
  const handleChartClick = (type, title, additionalData = {}) => {
    setModalType(type)
    setModalTitle(title)
    setModalData(additionalData)
    setModalOpen(true)
  }

  // Load financial insights data
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Mock data for demonstration
      const mockData = {
        financial_summary: {
          total_rfqs: 45,
          total_budget: 2500000,
          total_final_amount: 2350000,
          total_paid_amount: 940000,
          total_pending_amount: 1410000,
          total_remaining_amount: 1410000,
          total_security_deposit: 235000,
          total_advance_payment: 470000,
          total_additional_payment: 50000
        },
        payment_trends: [
          { month: '2024-01', status: 'paid', total_paid: 200000 },
          { month: '2024-01', status: 'pending', total_requested: 300000 },
          { month: '2024-02', status: 'paid', total_paid: 350000 },
          { month: '2024-02', status: 'pending', total_requested: 250000 },
          { month: '2024-03', status: 'paid', total_paid: 390000 },
          { month: '2024-03', status: 'pending', total_requested: 400000 }
        ],
        invoice_analytics: [
          {
            year: 2024,
            year_name: '2024',
            total_invoice_amount: 150000,
            total_taxable_amount: 135000,
            total_gst_amount: 15000,
            invoice_count: 12
          },
          {
            year: 2025,
            year_name: '2025',
            total_invoice_amount: 39200,
            total_taxable_amount: 36100,
            total_gst_amount: 3100,
            invoice_count: 3
          }
        ]
      }

      // Use real API calls
      try {
        // Load main financial insights
        const response = await getRFQFinancialInsights(filters)
        console.log("Financial insights response:", response)

        // Load Invoice Analytics data using new dedicated API
        const invoiceAnalyticsResponse = await getInvoiceAnalyticsGraphData({
          start_date: filters?.start_date,
          end_date: filters?.end_date,
          project_id: filters?.project_id,
          vendor_id: filters?.vendor_id,
          status_id: filters?.status_id,
          serviceHeadId: filters?.serviceHeadId,
          serviceSubheadId: filters?.serviceSubheadId
        })

        let finalData = mockData // Start with mock data as fallback

        if (response && response.status) {
          finalData = response.data
          console.log("Financial insights loaded successfully:", response.data)
        } else {
          console.warn("Failed to load main financial insights, using mock data")
        }

        // Update Invoice Analytics data with real API data
        if (invoiceAnalyticsResponse && invoiceAnalyticsResponse.status) {
          // Transform the API response to match the expected format for annual data
          const invoiceAnalyticsData = invoiceAnalyticsResponse.data.annual_data.map(item => ({
            year: item.year,
            year_name: item.year_name,
            total_invoice_amount: item.total_amount,
            total_taxable_amount: item.taxable_amount,
            total_gst_amount: item.gst_amount,
            invoice_count: item.invoice_count
          }))

          finalData.invoice_analytics = invoiceAnalyticsData
          console.log("Invoice Analytics data loaded (Annual):", invoiceAnalyticsData)
        } else {
          console.warn("Failed to load Invoice Analytics data:", invoiceAnalyticsResponse?.message)
        }

        setData(finalData)

      } catch (apiError) {
        console.warn("Failed to load financial insights, using mock data:", apiError)
        setData(mockData)
      }
    } catch (err) {
      console.error("Financial insights loading error:", err)
      setError(err.message || "Failed to load financial insights")
    } finally {
      setLoading(false)
    }
  }

  // Load data when filters change
  useEffect(() => {
    loadData()
  }, [filters])

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-3">
          <h5>Loading Financial Insights...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert color="danger" className="text-center">
        <h4 className="alert-heading">Error Loading Financial Insights</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bx bx-refresh me-1"></i>
          Try Again
        </button>
      </Alert>
    )
  }

  if (!data || !data.financial_summary) {
    return (
      <div className="text-center py-5">
        <i className="bx bx-money display-4 text-muted"></i>
        <h5 className="mt-3">No Financial Data Available</h5>
        <p className="text-muted">Please adjust your filters and try again.</p>
      </div>
    )
  }

  // Safe data destructuring with defaults
  const {
    financial_summary = {},
    invoice_analytics = []
  } = data || {}







  // Prepare invoice analytics chart
  const invoiceChartOptions = {
    chart: {
      type: 'bar',
      height: 300,
      events: {
        dataPointSelection: function(event, chartContext, config) {
          const selectedYear = invoice_analytics[config.dataPointIndex]?.year
          const selectedStatus = config.seriesIndex === 0 ? 'taxable_amount' : 'gst_amount'
          const modalTitle = config.seriesIndex === 0 ?
            `Taxable Amount Details - ${selectedYear}` :
            `GST Amount Details - ${selectedYear}`

          handleChartClick('invoice_analytics', modalTitle, {
            year: selectedYear,
            invoice_status: selectedStatus
          })
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded'
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: invoice_analytics.map(item => item.year_name),
      title: {
        text: 'Year'
      }
    },
    yaxis: {
      title: {
        text: 'Amount (₹)'
      },
      labels: {
        formatter: function (val) {
          return formatCurrency(val)
        }
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return formatCurrency(val)
        }
      }
    },
    colors: getChartColors()
  }

  const invoiceChartSeries = [
    {
      name: 'Taxable Amount',
      data: invoice_analytics.map(item => parseFloat(item.total_taxable_amount) || 0)
    },
    {
      name: 'GST Amount',
      data: invoice_analytics.map(item => parseFloat(item.total_gst_amount) || 0)
    }
  ]

  return (
    <div className="financial-insights fade-in">
      {/* Financial Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card
            className="metric-card clickable-card"
            onClick={() => handleCardClick('total_budget', 'Total Budget Details')}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="financial_insights_total_budget"
                title="Total Budget - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <div className="text-center">
              <div className="metric-value">{formatCurrency(financial_summary.total_budget)}</div>
              <div className="metric-label">Total Budget</div>
              <small className="text-muted">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="metric-card info clickable-card"
            onClick={() => handleCardClick('total_final_amount', 'Total Final Amount Details')}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="financial_insights_total_final_amount"
                title="Total Final Amount - Business Logic & Calculation"
                size="sm"

              />
            </div>
            <div className="text-center">
              <div className="metric-value">{formatCurrency(financial_summary.total_final_amount)}</div>
              <div className="metric-label">Total Final Amount</div>
              <small className="text-muted">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="metric-card success clickable-card"
            onClick={() => handleCardClick('total_paid', 'Total Paid Amount Details')}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="financial_insights_total_paid"
                title="Total Paid - Business Logic & Calculation"
                size="sm"

              />
            </div>
            <div className="text-center">
              <div className="metric-value">{formatCurrency(financial_summary.total_paid_amount)}</div>
              <div className="metric-label">Total Paid</div>
              <small className="text-muted">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="metric-card warning clickable-card"
            onClick={() => handleCardClick('pending_amount', 'Pending Amount Details')}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="financial_insights_pending_amount"
                title="Pending Amount - Business Logic & Calculation"
                size="sm"

              />
            </div>
            <div className="text-center">
              <div className="metric-value">{formatCurrency(financial_summary.total_pending_amount)}</div>
              <div className="metric-label">Pending Amount</div>
              <small className="text-muted">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>

      </Row>

      {/* Invoice Analytics and Financial Summary Details Side by Side */}
      <Row className="mb-4">
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-bar-chart-alt me-2"></i>
                  Invoice Analytics
                </span>
                <InfoButton
                  type="financial_insights"
                  title="Invoice Analytics - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              {invoice_analytics && invoice_analytics.length > 0 ? (
                <Chart
                  options={invoiceChartOptions}
                  series={invoiceChartSeries}
                  type="bar"
                  height={300}
                />
              ) : (
                <div className="text-center py-4">
                  <i className="bx bx-receipt display-4 text-muted"></i>
                  <p className="text-muted mt-2">No invoice data available</p>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-table me-2"></i>
                  Financial Summary Details
                </span>
                <InfoButton
                  type="financial_insights"
                  title="Financial Summary Table - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              <div className="table-responsive">
                <Table className="table-nowrap mb-0"style={{height:"313px"}}>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      onClick={() => handleCardClick('financial_summary_all', 'Financial Summary - All RFQ Details')}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                      title="Click to view all RFQ financial details"
                    >
                      <td><strong>Total Budget</strong></td>
                      <td>{formatCurrency(financial_summary.total_budget)}</td>
                    </tr>
                    <tr
                      onClick={() => handleCardClick('financial_summary_all', 'Financial Summary - All RFQ Details')}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                      title="Click to view all RFQ financial details"
                    >
                      <td><strong>Final + Additional Amount</strong></td>
                      <td>{formatCurrency(financial_summary.total_final_amount)}</td>
                    </tr>
                    <tr
                      onClick={() => handleCardClick('financial_summary_all', 'Financial Summary - All RFQ Details')}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                      title="Click to view all RFQ financial details"
                    >
                      <td><strong>Paid Amount</strong></td>
                      <td>{formatCurrency(financial_summary.total_paid_amount)}</td>
                    </tr>
                    <tr
                      onClick={() => handleCardClick('financial_summary_all', 'Financial Summary - All RFQ Details')}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                      title="Click to view all RFQ financial details"
                    >
                      <td><strong>Pending Amount</strong></td>
                      <td>{formatCurrency(financial_summary.total_pending_amount)}</td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>



      {/* Financial Detail Modal */}
      <FinancialDetailModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        type={modalType}
        title={modalTitle}
        filters={filters}
        additionalData={modalData}
      />
    </div>
  )
}

export default FinancialInsights
